import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  HelpCircle,
  ImageOff,
  Loader2,
  Users,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pill } from '@/components/ui/pill';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { Combobox } from '@/components/ui/combobox';
import { FilterField } from '@/components/ui/filter-field';
import { channelLabel } from '@/components/ui/tag';
import { PlatformIcon } from '@/components/PlatformIcon';
import {
  useReviewScreenshot,
  useScreenshotAuditQueue,
  type AuditFilters,
} from '@/hooks/useScreenshotAudit';
import { useAdminTeams } from '@/hooks/useAdmin';
import { useMainScopeLabel, useTeamScope } from '@/hooks/useTeamScope';
import { useUrlState } from '@/hooks/useUrlState';
import { POSITIVE_REPLY } from '@/lib/outreach-copy';
import {
  GLOSSARY,
  creatorTypeLabel,
  inviteStatusLabel,
  teamLabel,
  verdictLabel,
  verdictTone,
} from '@/lib/labels';
import { formatDate } from '@/lib/dates';
import { formatMoney } from '@/lib/money';
import { clampCursor, inviteLink, withoutReviewed } from '@/lib/moneyPages';
import { VerdictButton } from '@/components/invites/VerdictButton';
import type { ScreenshotAuditItem, ScreenshotVerdict } from '@/api/types';

/**
 * Proof review.
 *
 * Built around throughput rather than completeness: the backlog is thousands
 * of images, so the design goal is "one keystroke per verdict, next image
 * already loaded". Everything else — filters, notes, history — is secondary to
 * not breaking that rhythm.
 *
 * The reviewer answers exactly one question per image, and it's stated on
 * screen so the bar doesn't drift between sessions: did this creator reply
 * asking to join?
 */
type QueueState = NonNullable<AuditFilters['state']>;

const STATE_OPTIONS: { value: QueueState; label: string }[] = [
  { value: 'pending', label: 'Needs review' },
  { value: 'APPROVED', label: verdictLabel('APPROVED') },
  { value: 'REJECTED', label: verdictLabel('REJECTED') },
  { value: 'UNCLEAR', label: verdictLabel('UNCLEAR') },
];

/**
 * Which invites the queue covers. "Still in play" leaves out expired,
 * rejected and hidden invites: a verdict on those changes nothing anyone is
 * paid, so they're the backlog to clear last.
 */
type Scope = 'live' | 'all';
const SCOPE_OPTIONS: { value: Scope; label: string }[] = [
  { value: 'live', label: 'Still in play' },
  { value: 'all', label: 'Everything' },
];

function parseState(raw: string): QueueState {
  return STATE_OPTIONS.some((o) => o.value === raw) ? (raw as QueueState) : 'pending';
}

/** The queue API serves at most 50 at a time; start smaller so the first page is quick. */
const FIRST_LIMIT = 25;
const MAX_LIMIT = 50;

export function AdminScreenshotAudit(): JSX.Element {
  // In the URL so Home's count links straight to the same view.
  const [url, setUrl] = useUrlState({ state: 'pending', show: 'live' });
  const state = parseState(url.state);
  const live = url.show !== 'all';
  const [limit, setLimit] = useState(FIRST_LIMIT);
  const [cursor, setCursor] = useState(0);
  const [note, setNote] = useState('');
  // Reviewed this session: dropped from the list at once, so a second
  // keypress can't review the same proof while the queue refetches.
  const [reviewed, setReviewed] = useState<ReadonlySet<string>>(() => new Set());
  const submitting = useRef(false);
  const { team, setTeam, locked, params: teamParams } = useTeamScope();
  const teams = useAdminTeams();
  const mainScopeLabel = useMainScopeLabel();

  // Newest first: a fresh invite's proof is the one most likely to still matter.
  // With no team picked, Influencer and Vyral proofs are left out like
  // everywhere else in the main views; pick the team to review theirs.
  const query = useScreenshotAuditQueue({ state, limit, live, order: 'newest', ...teamParams });
  const review = useReviewScreenshot();

  const items = useMemo(() => withoutReviewed(query.data?.items ?? [], reviewed), [query.data, reviewed]);
  const safeCursor = clampCursor(cursor, items.length);
  const current = items[safeCursor] ?? null;
  const isHistory = state !== 'pending';

  const teamName = useCallback(
    (code: string) => teamLabel(code, teams.data?.find((t) => t.code === code)?.displayName),
    [teams.data],
  );

  // Reset position when the filter changes — index 3 of the old list means
  // nothing in the new one.
  useEffect(() => {
    setCursor(0);
    setLimit(FIRST_LIMIT);
    setReviewed(new Set());
  }, [state, team, live]);

  // Keep the cursor inside the list as reviewed rows drop out.
  useEffect(() => {
    if (cursor !== safeCursor) setCursor(safeCursor);
  }, [cursor, safeCursor]);

  // Preload the next two images so a verdict is never followed by a spinner.
  // This is the difference between ~2s and ~0.2s per review.
  useEffect(() => {
    for (const next of items.slice(safeCursor + 1, safeCursor + 3)) {
      if (!next.screenshotUrl) continue;
      const img = new Image();
      img.src = next.screenshotUrl;
    }
  }, [items, safeCursor]);

  const submit = useCallback(
    (verdict: ScreenshotVerdict) => {
      if (!current || review.isPending || submitting.current) return;
      submitting.current = true;
      const target = current.targetIdentifier;
      const publicId = current.invitePublicId;
      review.mutate(
        { invitePublicId: publicId, verdict, note: note.trim() || null },
        {
          onSuccess: () => {
            toast.success(`@${target}: ${verdictLabel(verdict)}`);
            setNote('');
            // Drop it locally; the cursor now points at the next proof.
            setReviewed((prev) => new Set(prev).add(publicId));
          },
          onError: (e: unknown) => toast.error((e as Error).message),
          onSettled: () => {
            submitting.current = false;
          },
        },
      );
    },
    [current, review, note],
  );

  // Keyboard is the primary interface. Ignored while typing a note so the
  // shortcuts can't fire mid-sentence.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      switch (e.key.toLowerCase()) {
        case 'a':
          e.preventDefault();
          submit('APPROVED');
          break;
        case 'r':
          e.preventDefault();
          submit('REJECTED');
          break;
        case 'u':
          e.preventDefault();
          submit('UNCLEAR');
          break;
        case 'arrowright':
          e.preventDefault();
          setCursor((c) => clampCursor(c + 1, items.length));
          break;
        case 'arrowleft':
          e.preventDefault();
          setCursor((c) => clampCursor(c - 1, items.length));
          break;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submit, items.length]);

  const counts = query.data?.counts;
  const loadedAll = (query.data?.items.length ?? 0) < limit;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Proof review"
        description="Check each invite's screenshot shows the creator replying and asking to join."
      />

      <div className="flex flex-wrap items-end gap-3">
        <FilterField label="Show">
          <Combobox
            className="w-full md:w-44"
            value={live ? 'live' : 'all'}
            onChange={(v) => setUrl({ show: v || 'live' })}
            options={SCOPE_OPTIONS}
            placeholder="Still in play"
          />
        </FilterField>
        <FilterField label="Verdict">
          <Combobox
            className="w-full md:w-44"
            value={state}
            onChange={(v) => setUrl({ state: v || 'pending' })}
            options={STATE_OPTIONS}
            placeholder="Needs review"
          />
        </FilterField>
        {!locked && (
          <FilterField label="Team">
            <Combobox
              className="w-full md:w-44"
              value={team}
              onChange={(v) => setTeam(v)}
              options={(teams.data ?? [])
                .filter((t) => t.isActive)
                .map((t) => ({ value: t.code, label: t.displayName }))}
              allowAll
              allLabel={mainScopeLabel}
            />
          </FilterField>
        )}
        <div className="flex items-center gap-2 pb-2 text-sm text-muted-foreground">
          <span>
            <span className="font-semibold text-foreground">
              {(query.data?.pendingTotal ?? 0).toLocaleString()}
            </span>{' '}
            {live ? 'still in play, waiting for review' : 'waiting for review'}
          </span>
          {counts && (
            <span className="text-xs">
              · {counts.approved.toLocaleString()} {verdictLabel('APPROVED')} ·{' '}
              {counts.rejected.toLocaleString()} {verdictLabel('REJECTED')} ·{' '}
              {counts.unclear.toLocaleString()} {verdictLabel('UNCLEAR')}
            </span>
          )}
        </div>
      </div>

      {/* The bar, restated. Review standards drift between sessions otherwise,
          and it must be the same rule the outreacher was shown when logging. */}
      <div className="rounded-lg border border-warning/40 bg-warning/[0.08] px-4 py-2.5 text-sm">
        <span className="font-bold text-foreground">{POSITIVE_REPLY.headline}</span>{' '}
        <span className="text-foreground/85">{POSITIVE_REPLY.qualifies}</span>{' '}
        <span className="text-foreground/85">{POSITIVE_REPLY.doesNot}</span>
      </div>

      {query.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading proofs…
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Check className="h-6 w-6" />}
          title={state === 'pending' ? 'Nothing left to review.' : 'Nothing in this view.'}
          hint={
            state === 'pending'
              ? query.isFetching
                ? 'Checking for more…'
                : live
                  ? 'Every invite still in play has a verdict. Show everything to work through expired and rejected ones.'
                  : 'Every invite with a screenshot has a verdict.'
              : 'Try a different filter.'
          }
        />
      ) : current ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <ScreenshotPane item={current} />

          <div className="space-y-4">
            <ContextCard item={current} teamName={teamName} />

            <Card>
              <CardContent className="space-y-3 pt-4">
                <Input
                  placeholder="Note (optional)"
                  aria-label="Review note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={500}
                />
                <div className="grid grid-cols-3 gap-2">
                  <VerdictButton
                    onClick={() => submit('APPROVED')}
                    disabled={review.isPending}
                    icon={<Check className="h-4 w-4" />}
                    label={verdictLabel('APPROVED')}
                    hint="A"
                    tone="approve"
                  />
                  <VerdictButton
                    onClick={() => submit('REJECTED')}
                    disabled={review.isPending}
                    icon={<X className="h-4 w-4" />}
                    label={verdictLabel('REJECTED')}
                    hint="R"
                    tone="reject"
                  />
                  <VerdictButton
                    onClick={() => submit('UNCLEAR')}
                    disabled={review.isPending}
                    icon={<HelpCircle className="h-4 w-4" />}
                    label={verdictLabel('UNCLEAR')}
                    hint="U"
                    tone="unclear"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Records a verdict only. "{verdictLabel('REJECTED')}" doesn't write off the invite
                  or change what anyone is owed; open the invite and use Reject invite for that.
                </p>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={safeCursor === 0}
                onClick={() => setCursor((c) => clampCursor(c - 1, items.length))}
              >
                <ChevronLeft className="mr-1 h-3.5 w-3.5" /> Prev
              </Button>
              <span className="text-xs text-muted-foreground">
                {safeCursor + 1} of {items.length} loaded
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={safeCursor >= items.length - 1}
                onClick={() => setCursor((c) => clampCursor(c + 1, items.length))}
              >
                Next <ChevronRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </div>
            {isHistory && !loadedAll && (
              limit < MAX_LIMIT ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  disabled={query.isFetching}
                  onClick={() => setLimit(MAX_LIMIT)}
                >
                  {query.isFetching && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                  Load more
                </Button>
              ) : (
                <p className="text-center text-xs text-muted-foreground">
                  Showing the latest {MAX_LIMIT}, the most this view loads at once. Filter by team
                  to see others.
                </p>
              )
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ScreenshotPane({ item }: { item: ScreenshotAuditItem }): JSX.Element {
  return (
    <Card className="overflow-hidden">
      <CardContent className="flex min-h-[60vh] items-center justify-center bg-muted/30 p-3">
        {item.screenshotUrl ? (
          <img
            src={item.screenshotUrl}
            alt={`Screenshot for @${item.targetIdentifier}`}
            className="max-h-[75vh] w-auto max-w-full rounded-md object-contain"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
            <ImageOff className="h-8 w-8" />
            <p>Couldn't load this image.</p>
            <p className="text-xs">Mark it {verdictLabel('UNCLEAR')} and move on.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ContextCard({
  item,
  teamName,
}: {
  item: ScreenshotAuditItem;
  teamName: (code: string) => string;
}): JSX.Element {
  return (
    <Card>
      <CardContent className="space-y-2 pt-4 text-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5 font-semibold">
            <PlatformIcon platform={item.channel} size={14} className="shrink-0 text-muted-foreground" />
            <span className="truncate">@{item.targetIdentifier}</span>
          </span>
          <Link
            to={inviteLink({ handle: item.targetIdentifier, openPublicId: item.invitePublicId })}
            className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            Open invite <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
        <Stake earned={item.lockedValue} />
        <Row label="Channel" value={channelLabel(item.channel)} />
        <Row label="Outreacher" value={`${item.inviterName} (${teamName(item.inviterTeam)})`} />
        <Row label="Logged" value={formatDate(item.createdAt)} />
        <Row label="Creator type" value={creatorTypeLabel(item.category) || '—'} />
        <Row label="Status" value={inviteStatusLabel(item.status) || '—'} />
        {item.contactCount > 1 && (
          <div className="flex items-start gap-1.5 rounded-md border border-warning/40 bg-warning/[0.08] px-2 py-1.5 text-xs">
            <Users className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
            <span>
              <span className="font-semibold">{item.contactCount} outreachers</span> messaged this
              creator. This invite was logged first, so the reply is worth a closer look.
            </span>
          </div>
        )}
        {item.verdict && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs text-muted-foreground">
            <span>Current verdict</span>
            <Pill tone={verdictTone(item.verdict)} icon={false}>
              {verdictLabel(item.verdict)}
            </Pill>
            {item.reviewedAt ? <span>on {formatDate(item.reviewedAt)}</span> : null}
            {item.reviewNote ? <span className="w-full">"{item.reviewNote}"</span> : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * What's riding on the verdict. The queue doesn't carry what's still owed on
 * the invite, so this shows what it has earned: the amount a failing proof
 * would put in question.
 */
function Stake({ earned }: { earned: string | null }): JSX.Element {
  const amount = earned ? parseFloat(earned) : 0;
  return (
    <div className="rounded-lg border border-brand/25 bg-brand/[0.06] px-3 py-2" title={GLOSSARY.earned}>
      {amount > 0 ? (
        <div className="flex items-baseline gap-1.5">
          <span className="font-mono text-lg font-medium tracking-[-0.03em] tabular-nums">
            ${formatMoney(amount)}
          </span>
          <span className="text-xs text-muted-foreground">earned on this invite so far</span>
        </div>
      ) : (
        <span className="text-xs text-muted-foreground">Nothing earned on this invite yet.</span>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
